import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  AccountBalance as ProcessIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { hasActionPermission } from '../utils/permissions';

const WithdrawalRequests = () => {
  const theme = useTheme();
  const { isDarkMode } = useCustomTheme();
  const [withdrawalRequests, setWithdrawalRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Get user permissions
  const getUserPermissions = () => {
    try {
      const user = localStorage.getItem('user');
      if (user) {
        const userData = JSON.parse(user);
        return userData.permissions || [];
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
    }
    return [];
  };

  const getUserType = () => {
    try {
      const user = localStorage.getItem('user');
      if (user) {
        const userData = JSON.parse(user);
        return userData.type || 'admin';
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
    }
    return 'admin';
  };

  const userPermissions = getUserPermissions();
  const userType = getUserType();
  
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({
    status: '',
    vendorId: '',
  });

  // Dialog states
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approveDialog, setApproveDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [processDialog, setProcessDialog] = useState(false);
  const [notes, setNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  // Snackbar states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const fetchWithdrawalRequests = useCallback(async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: '10',
        ...filters,
      });

      const response = await fetch(`/api/withdrawal/admin/withdrawal-requests?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setWithdrawalRequests(data.data);
        setTotalPages(data.pagination.totalPages);
        setTotalItems(data.pagination.totalItems);
      } else {
        throw new Error('Failed to fetch withdrawal requests');
      }
    } catch (error) {
      console.error('Error fetching withdrawal requests:', error);
      showSnackbar('Failed to fetch withdrawal requests', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch('/api/withdrawal/admin/withdrawal-stats', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchWithdrawalRequests();
    fetchStats();
  }, [fetchWithdrawalRequests, fetchStats]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setDetailsDialog(true);
  };

  const handleApprove = async () => {
    try {
      setProcessing(true);
      const response = await fetch(`/api/withdrawal/admin/withdrawal-request/${selectedRequest._id}/approve`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
        },
        body: JSON.stringify({ notes }),
      });

      if (response.ok) {
        showSnackbar('Withdrawal request approved successfully');
        setApproveDialog(false);
        setNotes('');
        fetchWithdrawalRequests();
        fetchStats();
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Failed to approve request');
      }
    } catch (error) {
      console.error('Error approving request:', error);
      showSnackbar(error.message, 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    try {
      setProcessing(true);
      const response = await fetch(`/api/withdrawal/admin/withdrawal-request/${selectedRequest._id}/reject`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
        },
        body: JSON.stringify({ 
          rejectionReason,
          notes 
        }),
      });

      if (response.ok) {
        showSnackbar('Withdrawal request rejected successfully');
        setRejectDialog(false);
        setRejectionReason('');
        setNotes('');
        fetchWithdrawalRequests();
        fetchStats();
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Failed to reject request');
      }
    } catch (error) {
      console.error('Error rejecting request:', error);
      showSnackbar(error.message, 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleProcess = async () => {
    try {
      setProcessing(true);
      const response = await fetch(`/api/withdrawal/admin/withdrawal-request/${selectedRequest._id}/process`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
        },
        body: JSON.stringify({ notes }),
      });

      if (response.ok) {
        const data = await response.json();
        showSnackbar(`Withdrawal processed successfully. Transfer Reference: ${data.data.transferDetails.transferReference}`);
        setProcessDialog(false);
        setNotes('');
        fetchWithdrawalRequests();
        fetchStats();
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Failed to process withdrawal');
      }
    } catch (error) {
      console.error('Error processing withdrawal:', error);
      showSnackbar(error.message, 'error');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'info';
      case 'completed':
        return 'success';
      case 'rejected':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ color: theme.palette.text.primary, mb: 3 }}>
        Vendor Withdrawal Requests
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ backgroundColor: theme.palette.background.paper }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Requests
              </Typography>
              <Typography variant="h4" sx={{ color: theme.palette.text.primary }}>
                {stats.totalRequests || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ backgroundColor: theme.palette.background.paper }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending Requests
              </Typography>
              <Typography variant="h4" sx={{ color: 'warning.main' }}>
                {stats.pendingRequests || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ backgroundColor: theme.palette.background.paper }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Amount
              </Typography>
              <Typography variant="h4" sx={{ color: theme.palette.text.primary }}>
                {formatCurrency(stats.totalAmount || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ backgroundColor: theme.palette.background.paper }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Platform Commission
              </Typography>
              <Typography variant="h4" sx={{ color: 'success.main' }}>
                {formatCurrency(stats.totalCommission || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: theme.palette.background.paper }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              label="Status"
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              setFilters({ status: '', vendorId: '' });
              setPage(1);
            }}
          >
            Reset Filters
          </Button>
        </Box>
      </Paper>

      {/* Withdrawal Requests Table */}
      <Paper sx={{ backgroundColor: theme.palette.background.paper }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Vendor</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Commission</TableCell>
                <TableCell>Net Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Request Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : withdrawalRequests.length === 0 ? (
                                 <TableRow>
                   <TableCell colSpan={8} align="center">
                     <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                       No withdrawal requests found
                     </Typography>
                   </TableCell>
                 </TableRow>
              ) : (
                withdrawalRequests.map((request) => (
                  <TableRow key={request._id}>
                                         <TableCell>
                       <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                         {request.vendorId?.businessName || 'N/A'}
                       </Typography>
                       <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                         {request.metadata?.vendorUsername || 'N/A'}
                       </Typography>
                     </TableCell>
                     <TableCell>
                       <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                         {request.metadata?.eventTitle || 'N/A'}
                       </Typography>
                       <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                         {formatDate(request.metadata?.eventDate)}
                       </Typography>
                     </TableCell>
                     <TableCell>
                       <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                         {formatCurrency(request.amount)}
                       </Typography>
                     </TableCell>
                     <TableCell>
                       <Typography variant="body2" sx={{ color: 'success.main' }}>
                         {formatCurrency(request.platformCommission)}
                       </Typography>
                     </TableCell>
                     <TableCell>
                       <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 'bold' }}>
                         {formatCurrency(request.netAmount)}
                       </Typography>
                     </TableCell>
                     <TableCell>
                       <Chip
                         label={request.status}
                         color={getStatusColor(request.status)}
                         size="small"
                       />
                     </TableCell>
                     <TableCell>
                       <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                         {formatDate(request.requestDate)}
                       </Typography>
                     </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                                                 <Tooltip title="View Details">
                           <IconButton
                             size="small"
                             onClick={() => handleViewDetails(request)}
                             sx={{ color: theme.palette.primary.main }}
                           >
                             <VisibilityIcon />
                           </IconButton>
                         </Tooltip>
                        
                        {request.status === 'pending' && (
                          <>
                            {hasActionPermission('approve_withdrawals', userPermissions, userType) && (
                              <Tooltip title="Approve">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedRequest(request);
                                    setApproveDialog(true);
                                  }}
                                  sx={{ color: 'success.main' }}
                                >
                                  <ApproveIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {hasActionPermission('reject_withdrawals', userPermissions, userType) && (
                              <Tooltip title="Reject">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedRequest(request);
                                    setRejectDialog(true);
                                  }}
                                  sx={{ color: 'error.main' }}
                                >
                                  <RejectIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        )}
                        
                        {request.status === 'approved' && (
                          <Tooltip title="Process to Utilita">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedRequest(request);
                                setProcessDialog(true);
                              }}
                              sx={{ color: 'info.main' }}
                            >
                              <ProcessIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, newPage) => setPage(newPage)}
              color="primary"
            />
          </Box>
        )}
      </Paper>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Withdrawal Request Details</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Vendor Information</Typography>
                  <Typography><strong>Business Name:</strong> {selectedRequest.vendorId?.businessName}</Typography>
                  <Typography><strong>Username:</strong> {selectedRequest.metadata?.vendorUsername}</Typography>
                  <Typography><strong>Email:</strong> {selectedRequest.metadata?.vendorEmail}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Event Information</Typography>
                  <Typography><strong>Event Title:</strong> {selectedRequest.metadata?.eventTitle}</Typography>
                  <Typography><strong>Event Date:</strong> {formatDate(selectedRequest.metadata?.eventDate)}</Typography>
                  <Typography><strong>Tickets Sold:</strong> {selectedRequest.metadata?.totalTicketsSold}</Typography>
                  <Typography><strong>Total Revenue:</strong> {formatCurrency(selectedRequest.metadata?.totalRevenue)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>Withdrawal Details</Typography>
                  <Grid container spacing={2}>
                                         <Grid item xs={12} md={4}>
                       <Typography><strong>Requested Amount:</strong></Typography>
                       <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                         {formatCurrency(selectedRequest.amount)}
                       </Typography>
                     </Grid>
                     <Grid item xs={12} md={4}>
                       <Typography><strong>Platform Commission:</strong></Typography>
                       <Typography variant="h6" sx={{ color: 'success.main' }}>
                         {formatCurrency(selectedRequest.platformCommission)}
                       </Typography>
                     </Grid>
                     <Grid item xs={12} md={4}>
                       <Typography><strong>Net Amount:</strong></Typography>
                       <Typography variant="h6" sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
                         {formatCurrency(selectedRequest.netAmount)}
                       </Typography>
                     </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Typography><strong>Status:</strong></Typography>
                  <Chip
                    label={selectedRequest.status}
                    color={getStatusColor(selectedRequest.status)}
                    sx={{ mt: 1 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography><strong>Request Date:</strong> {formatDate(selectedRequest.requestDate)}</Typography>
                  {selectedRequest.processedDate && (
                    <Typography><strong>Processed Date:</strong> {formatDate(selectedRequest.processedDate)}</Typography>
                  )}
                  {selectedRequest.transferReference && (
                    <Typography><strong>Transfer Reference:</strong> {selectedRequest.transferReference}</Typography>
                  )}
                  {selectedRequest.rejectionReason && (
                    <Typography><strong>Rejection Reason:</strong> {selectedRequest.rejectionReason}</Typography>
                  )}
                  {selectedRequest.notes && (
                    <Typography><strong>Notes:</strong> {selectedRequest.notes}</Typography>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={approveDialog} onClose={() => setApproveDialog(false)}>
        <DialogTitle>Approve Withdrawal Request</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to approve this withdrawal request?
          </Typography>
                     <Typography variant="h6" sx={{ color: theme.palette.primary.main, my: 2 }}>
             {selectedRequest && formatCurrency(selectedRequest.netAmount)} will be transferred to vendor's Utilita account
           </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes (Optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog(false)} disabled={processing}>
            Cancel
          </Button>
          <Button
            onClick={handleApprove}
            variant="contained"
            color="success"
            disabled={processing}
            startIcon={processing ? <CircularProgress size={20} /> : null}
          >
            {processing ? 'Approving...' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)}>
        <DialogTitle>Reject Withdrawal Request</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to reject this withdrawal request?
          </Typography>
          <TextField
            fullWidth
            required
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes (Optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(false)} disabled={processing}>
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            variant="contained"
            color="error"
            disabled={processing || !rejectionReason}
            startIcon={processing ? <CircularProgress size={20} /> : null}
          >
            {processing ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Process Dialog */}
      <Dialog open={processDialog} onClose={() => setProcessDialog(false)}>
        <DialogTitle>Process Withdrawal to Utilita</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Process this withdrawal to the vendor's Utilita account?
          </Typography>
                     <Typography variant="h6" sx={{ color: theme.palette.primary.main, my: 2 }}>
             {selectedRequest && formatCurrency(selectedRequest.netAmount)} will be credited to vendor's balance
           </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes (Optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProcessDialog(false)} disabled={processing}>
            Cancel
          </Button>
          <Button
            onClick={handleProcess}
            variant="contained"
            color="info"
            disabled={processing}
            startIcon={processing ? <CircularProgress size={20} /> : null}
          >
            {processing ? 'Processing...' : 'Process'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WithdrawalRequests; 